import type { InvestigationExecution, InvestigationLabels } from '@/frontend/features/Fde/investigations/types'

const count = (value: unknown) => typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.round(value).toLocaleString('pt-BR') : null

/** Counts completed reads, not hypotheses, distinct documents or research
 * coverage. Failed attempts and model calls never inflate this activity cue. */
export function investigationReadCounts(executions: InvestigationExecution[]) {
  const completed = executions.filter((execution) => execution.status === 'succeeded')
  return {
    queries: completed.filter((execution) => ['sql', 'query', 'profile'].includes(execution.kind)).length,
    documentReads: completed.filter((execution) => ['read_project_document', 'document'].includes(execution.kind)).length,
  }
}

/** Provider usage may gain arbitrary fields. Only explicit numeric counters and
 * controlled transport metadata enter this presentation; never dump the object.
 */
export function executionMetadata(execution: InvestigationExecution, copy: InvestigationLabels) {
  const usage = execution.usage ?? {}
  const rows: Array<[string, string | null]> = [
    [copy.metadata_elapsed, count(execution.elapsed_ms)],
    [copy.metadata_input_tokens, count(usage.input_tokens ?? usage.prompt_tokens)],
    [copy.metadata_output_tokens, count(usage.output_tokens ?? usage.completion_tokens)],
    [copy.metadata_total_tokens, count(usage.total_tokens)],
  ]
  if (typeof execution.request_sent === 'boolean') rows.push([copy.metadata_request_sent, execution.request_sent ? copy.yes : copy.no])
  if (typeof execution.http_status === 'number' && execution.http_status >= 100 && execution.http_status <= 599) rows.push([copy.metadata_http_status, String(execution.http_status)])
  if (execution.error_category) rows.push([copy.metadata_error_category, copy[`error_category_${execution.error_category}`] || copy.error_category_unknown])
  if (execution.result_hash && /^[a-f\d]{64}$/i.test(execution.result_hash)) {
    // A syntactically valid digest is not evidence of a reproducible encoding.
    // Legacy and unknown versions must not inherit the canonical-v1 label.
    const hashLabel = execution.result_hash_version === 'canonical_json_v1' ? copy.metadata_result_hash
      : execution.result_hash_version ? copy.metadata_result_hash_unknown : copy.metadata_result_hash_legacy
    rows.push([hashLabel, execution.result_hash])
  }
  return rows.filter((row): row is [string, string] => row[1] !== null)
}
